import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Mail,
  MessageSquare,
  Image,
  Bell,
  Calendar,
  User,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

// Custom Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ value, onChange, options, placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover focus:outline-none focus:ring-2 focus:ring-gray-400 min-w-[120px]"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[140px] bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg py-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors ${
                value === option.value 
                  ? 'font-medium text-gray-900 dark:text-dark-text-primary bg-gray-50 dark:bg-dark-bg-hover' 
                  : 'text-gray-700 dark:text-dark-text-secondary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Types
interface AuditLogOwner {
  id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
}

interface AuditLogPet {
  id: string;
  name: string | null;
}

interface AuditLogAudience {
  campaign_id: string | null;
  campaign_name: string | null;
  list_id: string | null;
  list_name: string | null;
}

interface AuditLogEntry {
  id: string;
  practice_id: string;
  owner_id: string | null;
  pet_id: string | null;
  notification_type: 'SMS' | 'MMS' | 'EMAIL' | 'PUSH';
  phone_number: string | null;
  email: string | null;
  success: boolean;
  opt_out: boolean;
  error_message: string | null;
  notification_source: string | null;
  source_id: string | null;
  message_preview: string | null;
  read: boolean;
  read_at: string | null;
  created_at: string;
  owner: AuditLogOwner | null;
  pet: AuditLogPet | null;
  campaign_id: string | null;
  audience: AuditLogAudience | null;
}

interface AudienceOption {
  list_id: string;
  list_name: string;
}

interface AuditLogListResponse {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface AuditLogStats {
  total_sent: number;
  total_success: number;
  total_failed: number;
  total_opt_out: number;
  by_type: Record<string, { success: number; failed: number; opt_out: number }>;
}

const NotificationAuditLog: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [audienceOptions, setAudienceOptions] = useState<AudienceOption[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Get filter values from URL params (RESTful state)
  const page = parseInt(searchParams.get('page') || '1', 10);
  const searchQuery = searchParams.get('search') || '';
  const notificationType = searchParams.get('type') || '';
  const successFilter = searchParams.get('status') || '';
  const optOutFilter = searchParams.get('opt_out') === 'true';
  const sourceFilter = searchParams.get('source') || '';
  const excludeBroadcast = searchParams.get('exclude_broadcast') === 'true';
  const audienceFilter = searchParams.get('audience') || '';
  const sortBy = searchParams.get('sort_by') || 'created_at';
  const sortOrder = (searchParams.get('sort_order') || 'desc') as 'asc' | 'desc';
  
  // Helper to update URL params
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || value === 'false') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      // Reset to page 1 when filters change (except when changing page itself)
      if (!('page' in updates) && Object.keys(updates).length > 0) {
        newParams.delete('page');
      }
      return newParams;
    });
  }, [setSearchParams]);
  
  // Setter functions that update URL
  const setPage = (p: number | ((prev: number) => number)) => {
    const newPage = typeof p === 'function' ? p(page) : p;
    updateParams({ page: newPage === 1 ? null : newPage.toString() });
  };
  const setSearchQuery = (v: string) => updateParams({ search: v || null });
  const setNotificationType = (v: string) => updateParams({ type: v || null });
  const setSuccessFilter = (v: string) => updateParams({ status: v || null });
  const setOptOutFilter = (v: boolean) => updateParams({ opt_out: v ? 'true' : null });
  const setSourceFilter = (v: string) => updateParams({ source: v || null });
  const setExcludeBroadcast = (v: boolean) => updateParams({ exclude_broadcast: v ? 'true' : null });
  const setAudienceFilter = (v: string) => updateParams({ audience: v || null });
  const setSortBy = (v: string) => updateParams({ sort_by: v === 'created_at' ? null : v });
  const setSortOrder = (v: 'asc' | 'desc') => updateParams({ sort_order: v === 'desc' ? null : v });
  
  // Show filters panel if any filter is active
  useEffect(() => {
    if (notificationType || successFilter || optOutFilter || sourceFilter || excludeBroadcast || audienceFilter) {
      setShowFilters(true);
    }
  }, [notificationType, successFilter, optOutFilter, sourceFilter, excludeBroadcast, audienceFilter]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25',
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (notificationType) params.append('notification_type', notificationType);
      if (successFilter === 'success') params.append('success', 'true');
      if (successFilter === 'failed') params.append('success', 'false');
      if (optOutFilter) params.append('opt_out', 'true');
      if (sourceFilter) params.append('notification_source', sourceFilter);
      if (excludeBroadcast) params.append('exclude_broadcast', 'true');
      if (audienceFilter) params.append('list_id', audienceFilter);
      
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_ENDPOINTS.NOTIFICATION_AUDIT.LIST}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data: AuditLogListResponse = await response.json();
        setLogs(data.items);
        setTotalPages(data.total_pages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, notificationType, successFilter, optOutFilter, sourceFilter, excludeBroadcast, audienceFilter, sortBy, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_ENDPOINTS.NOTIFICATION_AUDIT.STATS}?days=30`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data: AuditLogStats = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchAudienceOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(API_ENDPOINTS.NOTIFICATION_AUDIT.AUDIENCES, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data: AudienceOption[] = await response.json();
        setAudienceOptions(data);
      }
    } catch (error) {
      console.error('Error fetching audience options:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchAudienceOptions();
  }, [fetchAudienceOptions]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is already reactive via URL params, just need to trigger fetch
    fetchLogs();
  };

  const clearFilters = () => {
    // Clear all filter params at once
    setSearchParams(new URLSearchParams());
  };

  // Handle column header click for sorting
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle order if same column
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      updateParams({ sort_by: column === 'created_at' ? null : column, sort_order: newOrder === 'desc' ? null : newOrder });
    } else {
      // New column, default to desc
      updateParams({ sort_by: column === 'created_at' ? null : column, sort_order: null });
    }
  };

  // Render sort indicator
  const SortIndicator = ({ column }: { column: string }) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-gray-300 dark:text-gray-600">↕</span>;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="w-3 h-3 ml-1 inline" />
      : <ChevronDown className="w-3 h-3 ml-1 inline" />;
  };

  // Handle stats card click to filter
  const handleStatsCardClick = (filter: 'all' | 'success' | 'failed' | 'optout') => {
    if (filter === 'all') {
      updateParams({ status: null, opt_out: null });
    } else if (filter === 'success') {
      updateParams({ status: 'success', opt_out: null });
    } else if (filter === 'failed') {
      updateParams({ status: 'failed', opt_out: null });
    } else if (filter === 'optout') {
      updateParams({ status: null, opt_out: 'true' });
    }
  };

  const getStatusIcon = (entry: AuditLogEntry) => {
    // Check for manual opt-in (subscribed)
    if (entry.notification_source === 'manual_opt_in') {
      return <CheckCircle className="w-4 h-4 text-teal-500" />;
    }
    if (entry.opt_out) {
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
    if (entry.success) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusBadge = (entry: AuditLogEntry) => {
    // Check for manual opt-in (subscribed)
    if (entry.notification_source === 'manual_opt_in') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
          Subscribed
        </span>
      );
    }
    if (entry.opt_out) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          Opted Out
        </span>
      );
    }
    if (entry.success) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          Delivered
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Failed
      </span>
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'SMS':
        return <MessageSquare className="w-4 h-4" />;
      case 'MMS':
        return <Image className="w-4 h-4" />;
      case 'EMAIL':
        return <Mail className="w-4 h-4" />;
      case 'PUSH':
        return <Bell className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatSourceName = (source: string | null) => {
    if (!source) return '-';
    if (source === 'no_visit_reminder') return 'Appt Due Reminder';
    return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        {/* Stats Cards - Clickable to filter */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-4">
            <button
              onClick={() => handleStatsCardClick('all')}
              className={`text-left rounded-lg p-4 transition-all ${
                !successFilter && !optOutFilter
                  ? 'bg-gray-100 dark:bg-dark-bg-tertiary ring-2 ring-gray-400 dark:ring-gray-500'
                  : 'bg-gray-50 dark:bg-dark-bg-tertiary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
              }`}
            >
              <div className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                {stats.total_sent}
              </div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">Total Sent (30d)</div>
            </button>
            <button
              onClick={() => handleStatsCardClick('success')}
              className={`text-left rounded-lg p-4 transition-all ${
                successFilter === 'success'
                  ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500'
                  : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
              }`}
            >
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.total_success}
              </div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">Delivered</div>
            </button>
            <button
              onClick={() => handleStatsCardClick('failed')}
              className={`text-left rounded-lg p-4 transition-all ${
                successFilter === 'failed'
                  ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500'
                  : 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
              }`}
            >
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats.total_failed}
              </div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">Failed</div>
            </button>
            <button
              onClick={() => handleStatsCardClick('optout')}
              className={`text-left rounded-lg p-4 transition-all ${
                optOutFilter
                  ? 'bg-amber-100 dark:bg-amber-900/30 ring-2 ring-amber-500'
                  : 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30'
              }`}
            >
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {stats.total_opt_out}
              </div>
              <div className="text-sm text-gray-500 dark:text-dark-text-muted">Opted Out</div>
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by phone, email, or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </form>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
              showFilters || notificationType || successFilter || optOutFilter || sourceFilter || excludeBroadcast
                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {(notificationType || successFilter || optOutFilter) && (
              <span className="w-5 h-5 flex items-center justify-center bg-orange-600 text-white text-xs rounded-full">
                {[notificationType, successFilter, optOutFilter].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && (
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-dark-border">
            <CustomDropdown
              value={notificationType}
              onChange={(val) => setNotificationType(val)}
              options={[
                { value: '', label: 'All Types' },
                { value: 'SMS', label: 'SMS' },
                { value: 'MMS', label: 'MMS' },
                { value: 'EMAIL', label: 'Email' },
                { value: 'PUSH', label: 'Push' },
              ]}
            />
            
            <CustomDropdown
              value={successFilter}
              onChange={(val) => setSuccessFilter(val)}
              options={[
                { value: '', label: 'All Status' },
                { value: 'success', label: 'Delivered' },
                { value: 'failed', label: 'Failed' },
              ]}
            />
            
            <CustomDropdown
              value={sourceFilter}
              onChange={(val) => setSourceFilter(val)}
              options={[
                { value: '', label: 'All Sources' },
                { value: 'broadcast', label: 'Broadcast' },
                { value: 'appointment_confirmation', label: 'Appt Confirmation' },
                { value: 'appointment_reminder', label: 'Appt Reminder' },
                { value: 'no_visit_reminder', label: 'Appt Due Reminder' },
                { value: 'appointment_reschedule', label: 'Appt Reschedule' },
                { value: 'form_trigger', label: 'Form Trigger' },
                { value: 'overdue_vaccine', label: 'Overdue Vaccine' },
              ]}
            />
            
            {audienceOptions.length > 0 && (
              <CustomDropdown
                value={audienceFilter}
                onChange={(val) => setAudienceFilter(val)}
                options={[
                  { value: '', label: 'All Audiences' },
                  ...audienceOptions.map(opt => ({
                    value: opt.list_id,
                    label: opt.list_name
                  }))
                ]}
              />
            )}
            
            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg-hover">
              <input
                type="checkbox"
                checked={excludeBroadcast}
                onChange={(e) => setExcludeBroadcast(e.target.checked)}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm">Exclude Broadcasts</span>
            </label>
            
            {(searchQuery || notificationType || successFilter || optOutFilter || sourceFilter || excludeBroadcast || audienceFilter) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary"
              >
                <X className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <FileText className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No notifications found</p>
            <p className="text-sm">Notification attempts will appear here</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
              <tr>
                <th 
                  onClick={() => handleSort('success')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-bg-hover select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center">Status <SortIndicator column="success" /></span>
                </th>
                <th 
                  onClick={() => handleSort('notification_type')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-bg-hover select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center">Type <SortIndicator column="notification_type" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Pet Owner
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Pet
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Audience
                </th>
                <th 
                  onClick={() => handleSort('read')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-bg-hover select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center">Read <SortIndicator column="read" /></span>
                </th>
                <th 
                  onClick={() => handleSort('created_at')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-bg-hover select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center">Date <SortIndicator column="created_at" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log)}
                      {getStatusBadge(log)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-dark-text-secondary">
                      {getTypeIcon(log.notification_type)}
                      <span className="text-sm">{log.notification_type}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-dark-text-primary">
                      {log.notification_type === 'EMAIL' ? log.email : log.phone_number || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.owner ? (
                      <button
                        onClick={() => navigate(`/dashboard/pet_owners/${log.owner?.id}`)}
                        className="flex items-center gap-2 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg px-2 py-1 -mx-2 -my-1 transition-colors"
                        title="View pet owner"
                      >
                        <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                          <User className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                        </div>
                        <span className="text-sm text-gray-900 dark:text-dark-text-primary hover:text-orange-600 dark:hover:text-orange-400">
                          {log.owner.full_name || 'Unknown'}
                        </span>
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.pet && log.owner ? (
                      <button
                        onClick={() => navigate(`/dashboard/pet_owners/${log.owner?.id}/pets/${log.pet?.id}`)}
                        className="text-sm text-gray-900 dark:text-dark-text-primary hover:text-teal-600 dark:hover:text-teal-400 hover:underline"
                        title="View pet"
                      >
                        {log.pet.name || 'Unknown'}
                      </button>
                    ) : log.pet ? (
                      <span className="text-sm text-gray-900 dark:text-dark-text-primary">
                        {log.pet.name || 'Unknown'}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                      {formatSourceName(log.notification_source)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.audience?.list_name ? (
                      <span 
                        className="text-sm text-gray-600 dark:text-dark-text-secondary cursor-pointer hover:text-gray-900 dark:hover:text-dark-text-primary"
                        onClick={() => setAudienceFilter(log.audience?.list_id || '')}
                        title={`Filter by: ${log.audience.list_name}`}
                      >
                        {log.audience.list_name}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.opt_out ? (
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                    ) : log.read ? (
                      <div className="flex items-center" title={log.read_at ? `Opened ${formatDate(log.read_at)}` : 'Opened'}>
                        <Eye className="w-4 h-4 text-green-500" />
                      </div>
                    ) : log.success ? (
                      <div className="flex items-center gap-1.5" title="Not opened yet">
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <Calendar className="w-3 h-3" />
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {log.error_message ? (
                      <div className="max-w-xs">
                        <span className="text-xs text-red-600 dark:text-red-400 truncate block" title={log.error_message}>
                          {log.error_message}
                        </span>
                      </div>
                    ) : log.message_preview ? (
                      <div className="max-w-xs">
                        <span className="text-xs text-gray-500 dark:text-dark-text-muted truncate block" title={log.message_preview}>
                          {log.message_preview}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">
              Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-700 dark:text-dark-text-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationAuditLog;

